import { Box, ClickAwayListener, IconButton } from '@mui/material'
import {
  Close,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff
} from '@mui/icons-material'
import { useState, useRef, useEffect } from 'react'
import { useTour } from '@reactour/tour'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { t } from 'i18next'
import { useTfmTourProvider } from './TFMTourProvider'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import AreYouSureDialog from 'components/dialogs/AreYouSureDialog'
import { isCypress } from 'utils/cypress'
import { isTestEnv } from 'utils/env'
import { set } from 'lodash'
import { useTheme } from '@mui/material/styles'

export interface VideoOnboardingContentProps {
  title: string
  videoUrl: string
  doNotMoveStep?: boolean
  conditionalStep?: (props: any) => void
  blockUserInteraction?: boolean
  nextButtonAction?: (props: any) => void
  position?: 'center' | 'bottom-right'
  isCompact?: boolean
  veryFirst?: boolean
  id?: string
  closeOnOutsideClick?: boolean
  onVideoEnded?: () => void
}

export function VideoOnboardingContent(props: VideoOnboardingContentProps) {
  const theme = useTheme()
  const tour = useTour()
  const { currentStep, steps, setCurrentStep, setIsOpen } = tour
  const providerContext = useTfmTourProvider()
  const {
    wasPlayed,
    setWasPlayed,
    wasUnMuted,
    setWasUnMuted,
    hasEnded,
    setHasEnded,
    isPlaying,
    setIsPlaying,
    isMuted,
    setIsMuted,
    showSureEndDialog,
    setShowSureEndDialog
  } = providerContext

  const videoRef = useRef<HTMLVideoElement>(null)

  const [showControls, setShowControls] = useState(false)
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  // listen for keyboard events, if user press space, call handleNext for test env only
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.code === 'Space') {
        const muted = isMuted
        handleNext()
        setIsMuted(muted)
      }
    }

    if (isTestEnv()) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      if (isTestEnv()) {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [currentStep, steps])

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted
      videoRef.current.loop = wasPlayed ? false : true
    }
  }, [isMuted])

  useEffect(() => {
    // Auto-play video when component mounts
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log('Autoplay prevented:', error)
      })
      setIsPlaying(true)
    }
  }, [props.videoUrl, props.title])

  function handleFirstEnable() {
    if (videoRef.current) {
      setIsMuted(false)
      setIsPlaying(true)
      setWasUnMuted(true)
      setWasPlayed(true)

      videoRef.current.currentTime = 0
      videoRef.current.muted = false
      videoRef.current.play()
    }
  }

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying && wasUnMuted === true) {
        videoRef.current.pause()
        setIsPlaying(false)
      } else {
        // If muted, restart from beginning and unmute
        setWasPlayed(true)
        if (isMuted && wasUnMuted === undefined) {
          handleFirstEnable()
        } else if (!isPlaying) {
          videoRef.current.play()
          setIsPlaying(true)
        }

        setHasEnded(false)
      }
    }
  }

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (isMuted && !hasEnded && !wasPlayed) {
      handlePlayPause()
    } else {
      if (videoRef.current) {
        videoRef.current.muted = !isMuted
        setIsMuted(!isMuted)
        setWasUnMuted(true)
      }
    }
  }

  const handleClose = () => {
    setShowSureEndDialog?.(true)
  }

  const handleNext = () => {
    if (props.doNotMoveStep) {
      if (props.conditionalStep) {
        props.conditionalStep(tour)
      }
      setIsOpen(false)
      return
    }

    if (currentStep === steps.length - 1) {
      setIsOpen(false)
      setIsPlaying(false)
    }

    setHasEnded(false)
    props.nextButtonAction?.(tour)
    setTimeout(
      () => {
        setCurrentStep((s) => s + 1)
      },
      props.nextButtonAction ? 50 : 1
    )

    props.onVideoEnded?.()
  }

  const handleMetadataLoaded = () => {
    // Video metadata loaded, no longer need to set duration
    if (videoRef.current) {
      setDuration(videoRef.current.duration)
    }
  }

  const handleTimeUpdate = () => {
    // Update current time
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime)
    }
  }

  const formatTime = (time: number) => {
    const seconds = Math.floor(time % 60)
    const minutes = Math.floor(time / 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handleMouseEnter = () => {
    setShowControls(true)
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
  }

  const handleMouseMove = () => {
    setShowControls(true)
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    // Hide controls after 3 seconds of no movement
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false)
    }, 3000)
  }

  const handleMouseLeave = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    setShowControls(false)
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => {
        if (props.closeOnOutsideClick) setIsOpen(false)
      }}
    >
      <Box
        id={props.id}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '200px',
          backgroundColor: theme.palette.card.primary,
          borderRadius: '30px',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          aspectRatio: '9 / 16'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Video Player */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            overflow: 'hidden'
          }}
        >
          <video
            ref={videoRef}
            src={props.videoUrl}
            loop={wasPlayed === undefined}
            muted={isMuted}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: hasEnded ? 'blur(10px)' : 'none',
              display: 'block'
            }}
            onLoadedMetadata={handleMetadataLoaded}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => {
              setIsPlaying(false)
              if (wasPlayed === true || videoRef.current?.loop === false) {
                setHasEnded(true)
                if (props.onVideoEnded) {
                  props.onVideoEnded()
                }
              }
            }}
          />

          {/* Play/Pause Button - Center of Video (fades with controls) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: showControls || hasEnded ? 1 : 0,
              transition: 'opacity 0.3s ease',
              zIndex: 3
            }}
            onClick={handlePlayPause}
          >
            <IconButton
              sx={{
                color: theme.palette.common.white,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)'
                },
                width: '60px',
                height: '60px'
              }}
            >
              {isPlaying && wasUnMuted !== undefined ? (
                <Pause sx={{ fontSize: '36px' }} />
              ) : (
                <PlayArrow sx={{ fontSize: '36px' }} />
              )}
            </IconButton>
          </Box>

          {/* Top Right Controls */}
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
              display: 'flex',
              flexDirection: 'column',

              opacity: showControls ? 1 : 0,
              transition: 'opacity 0.3s ease',
              pointerEvents: showControls ? 'auto' : 'none',

              zIndex: 4
            }}
          >
            {/* Close Button - Always Visible */}
            <IconButton
              onClick={handleClose}
              sx={{
                color: theme.palette.common.white,

                width: '40px',
                height: '40px'
              }}
              size="small"
            >
              <Close sx={{ fontSize: '20px' }} />
            </IconButton>
          </Box>

          {/* Left Side - Time Countdown (visible on hover or when ended) */}
          <Box
            sx={{
              position: 'absolute',
              top: '12px',
              left: '10px',
              opacity: showControls ? 1 : 0,
              transition: 'opacity 0.3s ease',
              pointerEvents: 'none',
              zIndex: 4
            }}
          >
            <Box
              sx={{
                color: theme.palette.common.white,

                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 500
              }}
            >
              {hasEnded
                ? formatTime(duration)
                : formatTime(duration - currentTime)}
            </Box>
          </Box>

          {/* Bottom - Mute Button (Always Visible) */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '5px',
              right: '5px',

              zIndex: 4
            }}
          >
            <IconButton
              onClick={handleToggleMute}
              sx={{
                color: theme.palette.common.white,

                width: '40px',
                height: '40px'
              }}
              size="small"
            >
              {isMuted ? (
                <VolumeOff sx={{ fontSize: '20px' }} />
              ) : (
                <VolumeUp sx={{ fontSize: '20px' }} />
              )}
            </IconButton>
          </Box>
        </Box>
        {/* Next Button - Top of Video */}
        <Box
          id="onboarding-next-button"
          onClick={handleNext}
          sx={{
            position: 'absolute',
            bottom: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 5,
            padding: '5px 16px',
            textAlign: 'center',
            color: theme.palette.common.white,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderRadius: '30px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: 500,
            opacity: hasEnded || isCypress() ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: hasEnded || isCypress() ? 'auto' : 'none'
          }}
        >
          {currentStep === steps.length - 1 ? t('continue') : t('nextTip')}
        </Box>
        <ConfirmationDialog
          title={''}
          content={t('areYouSureGuideTour')}
          rightButton={t('continueTour')}
          leftButton={t('quitTour')}
          leftButtonColor="black"
          handleRightButton={() => {
            setShowSureEndDialog?.(false)
          }}
          handleLeftButton={() => {
            setShowSureEndDialog?.(false)
            setIsOpen(false)
            LocalStorage.set(LocalStorageKey.USER_FINSHED_ONBOARDING, '1')
          }}
          open={showSureEndDialog || false}
        />
      </Box>
    </ClickAwayListener>
  )
}
